import React, { useEffect, useState, useMemo } from "react";
import { format, isSameDay } from "date-fns";
import { DayPicker } from "react-day-picker";
import "react-day-picker/dist/style.css";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { CalendarClock, Users, User, Layers, Eye } from "lucide-react";

export default function CalendarPage() {
    const navigate = useNavigate();
    const [clients, setClients] = useState([]);
    const [selectedDate, setSelectedDate] = useState(new Date());

    const STAGE_CONFIG = {
        Enquiry: { label: "Enquiry Received", color: "bg-blue-50 text-blue-700 border-blue-200" },
        InProgress: { label: "In Progress / Shooting", color: "bg-amber-50 text-amber-700 border-amber-200" },
        PostProduction: { label: "Post-Production", color: "bg-purple-50 text-purple-700 border-purple-200" },
        Completed: { label: "Completed & Delivered", color: "bg-emerald-50 text-emerald-700 border-emerald-200" },
    };

    useEffect(() => {
        api.get("/clients").then((res) => setClients(res.data || []));
    }, []);

    // Flatten all events into a single array for easier lookup
    const allEvents = useMemo(() => {
        return (clients || []).flatMap((client) =>
            (client.events || []).map((ev) => ({
                ...ev,
                clientName: client.name,
                clientId: client.id,
                currentStage: client.current_stage || "Enquiry",
            }))
        );
    }, [clients]);

    // Find events for the selected day
    const eventsOnSelectedDay = useMemo(() => {
        if (!selectedDate) return [];
        return allEvents.filter((ev) =>
            ev.event_date && isSameDay(new Date(ev.event_date), selectedDate)
        );
    }, [allEvents, selectedDate]);

    // Get unique dates for styling booked days
    const bookedDates = useMemo(() => {
        return allEvents.filter((ev) => ev.event_date).map((ev) => new Date(ev.event_date));
    }, [allEvents]);

    return (
        <div className="p-4 sm:p-8 max-w-5xl mx-auto space-y-6">
            <h1 className="text-2xl font-serif font-bold text-gray-900">Studio Calendar</h1>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
                {/* Calendar Side */}
                <Card className="md:col-span-1 shadow-sm border-gray-200">
                    <CardContent className="p-4 flex justify-center">
                        <div
                            style={{
                                "--rdp-accent-color": "#f97316", // Orange 500
                                "--rdp-background-color": "#ffedd5", // Orange 100
                            }}
                        >
                            <DayPicker
                                mode="single"
                                selected={selectedDate}
                                onSelect={(d) => d && setSelectedDate(d)}
                                modifiers={{ booked: bookedDates }}
                                modifiersStyles={{
                                    booked: {
                                        fontWeight: "bold",
                                        textDecoration: "underline text-decoration-color: #f97316",
                                        color: "#ea580c"
                                    }
                                }}
                            />
                        </div>
                    </CardContent>
                </Card>

                {/* Details Side */}
                <div className="md:col-span-2 space-y-4">
                    <h2 className="text-lg font-semibold border-b pb-2 text-gray-900 flex items-center justify-between">
                        <span>Events for {selectedDate ? format(selectedDate, "PPP") : "Select a date"}</span>
                        <Badge className="bg-orange-50 text-orange-700 border-orange-200 font-normal">
                            {eventsOnSelectedDay.length} Event{eventsOnSelectedDay.length !== 1 && "s"}
                        </Badge>
                    </h2>

                    {eventsOnSelectedDay.length === 0 ? (
                        <div className="text-gray-500 italic p-6 bg-gray-50/50 border border-dashed border-gray-200 rounded-lg text-center text-xs sm:text-sm">
                            No events scheduled for this day.
                        </div>
                    ) : (
                        eventsOnSelectedDay.map((ev, i) => {
                            const stageInfo = STAGE_CONFIG[ev.currentStage] || STAGE_CONFIG.Enquiry;

                            return (
                                <Card key={ev.id || i} className="shadow-none border-gray-200 hover:border-gray-300 transition-all">
                                    <CardHeader className="pb-2 pt-4">
                                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                                            <div>
                                                <CardTitle className="text-base font-serif text-gray-900">
                                                    {ev.event_name}
                                                </CardTitle>
                                                <div className="text-xs text-gray-500 mt-0.5">
                                                    Client: <span className="font-medium text-gray-800">{ev.clientName}</span>
                                                </div>
                                            </div>

                                            {/* Existing Project Stage Badge */}
                                            <div className="flex items-center gap-2 shrink-0">
                                                <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${stageInfo.color}`}>
                                                    <Layers className="w-3 h-3" />
                                                    {stageInfo.label}
                                                </span>
                                            </div>
                                        </div>
                                    </CardHeader>

                                    <CardContent className="text-sm space-y-3">
                                        <div className="pt-2 border-t border-gray-100">
                                            <div className="flex items-center gap-2 font-medium mb-2 text-xs text-gray-700">
                                                <Users className="w-3.5 h-3.5 text-gray-400" /> Assigned Team:
                                            </div>
                                            {ev.assignments?.length > 0 ? (
                                                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                                    {ev.assignments.map((a, idx) => (
                                                        <li key={a.id || idx} className="flex items-center gap-2 bg-gray-50 p-2 rounded-md border border-gray-100 text-xs">
                                                            <User className="w-3.5 h-3.5 text-orange-500 shrink-0" />
                                                            <span className="truncate font-medium text-gray-800">{a.staff_name}</span>
                                                            <span className="text-[10px] text-gray-400">({a.role})</span>
                                                        </li>
                                                    ))}
                                                </ul>
                                            ) : (
                                                <p className="text-xs text-gray-400 italic">No staff assigned.</p>
                                            )}
                                        </div>

                                        {/* View Client Details Button */}
                                        <div className="pt-3 border-t border-gray-100 flex justify-end">
                                            <Button 
                                                variant="outline" 
                                                size="sm" 
                                                onClick={() => navigate(`/clients/${ev.clientId}`)}
                                                className="h-8 text-xs border-gray-200 text-gray-700 hover:text-orange-600 hover:border-orange-200 hover:bg-orange-50/50 transition-colors gap-1.5 cursor-pointer"
                                            >
                                                <Eye className="w-3.5 h-3.5 text-gray-500" />
                                                View Client Details
                                            </Button>
                                        </div>
                                    </CardContent>
                                </Card>
                            );
                        })
                    )}
                </div>
            </div>
        </div>
    );
}